import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import {
  useForkFormTemplate,
  useDeleteFormTemplate,
  type FormTemplateDeleteConflictError,
} from "@/hooks/use-form-templates";
import {
  useFormTemplate,
  useTemplateVersions,
  useSaveDraft,
  type FormTemplateDraftPut,
  type FormTemplateVersion,
} from "@/hooks/use-form-template";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { FormRenderer, parseFormDefinition } from "@njbsoft/form-renderer";
import { renderFacilityRefSlot } from "@/components/form-renderer/facility-ref-slot";
import { BUILDER_SCHEMA_VERSION } from "@njbsoft/form-builder";
import { VersionHistoryCard } from "@/components/form-templates/version-history-card";
import {
  TemplateMetaForm,
  type TemplateMetaValues,
} from "@/components/form-templates/template-meta-form";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/use-permissions";
import {
  PageHeader,
  Breadcrumbs,
  Badge,
  Button,
  Card,
  EmptyState,
  EmptyValue,
  PageSkeleton,
  Tabs,
  DialogShell,
  type TabDef,
} from "@/components/ui";

type TemplateTab = "definition" | "versions";

const ALLOWED_TABS: TemplateTab[] = ["definition", "versions"];

interface TemplateDetailSearch {
  tab?: TemplateTab;
}

function parseTab(value: unknown): TemplateTab | undefined {
  if (typeof value !== "string") return undefined;
  return ALLOWED_TABS.find((t) => t === value);
}

export const Route = createFileRoute("/_authenticated/test-program/templates/$id/")({
  component: FormTemplateDetailPage,
  validateSearch: (search: Record<string, unknown>): TemplateDetailSearch => ({
    tab: parseTab(search.tab),
  }),
});

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** The latest published row wins the preview; a never-published family previews its draft. */
function pickPreviewVersion(versions: FormTemplateVersion[]): {
  version: FormTemplateVersion | undefined;
  isDraftFallback: boolean;
} {
  const published = versions
    .filter((v) => v.status === "published")
    .sort((a, b) => b.version - a.version)[0];
  if (published) return { version: published, isDraftFallback: false };
  const draft = versions.find((v) => v.status === "draft");
  return { version: draft, isDraftFallback: Boolean(draft) };
}

function FormTemplateDetailPage() {
  const { t, i18n } = useTranslation(["form-templates", "common", "nav", "vocabulary"]);
  const toasts = useToast();
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();

  const { can } = usePermissions();
  const canCreate = can("form_templates", "create");
  const canUpdate = can("form_templates", "update");
  const canDelete = can("form_templates", "delete");

  const { data: template, isLoading, isError } = useFormTemplate(id);
  useRecentPageTitle(template?.title ?? undefined);
  const versionsQuery = useTemplateVersions(id);
  const saveDraftMutation = useSaveDraft(id);
  const forkMutation = useForkFormTemplate(id);
  const deleteMutation = useDeleteFormTemplate(id);

  const [forkOpen, setForkOpen] = useState(false);
  const [forkSlugConflict, setForkSlugConflict] = useState<string | null>(null);
  const [forkError, setForkError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteEnvelope, setDeleteEnvelope] = useState<FormTemplateDeleteConflictError | null>(
    null,
  );

  const activeTab: TemplateTab = tab ?? "definition";

  const setActiveTab = (next: TemplateTab) => {
    void navigate({
      to: "/test-program/templates/$id",
      params: { id },
      search: next === "definition" ? {} : { tab: next },
      replace: true,
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <PageSkeleton />
      </div>
    );
  }

  if (isError || !template) {
    return (
      <div className="p-6">
        <Breadcrumbs
          items={[
            {
              label: t("testProgram.templates", { ns: "nav" }),
              to: "/test-program",
              search: { tab: "templates" },
            },
          ]}
        />
        <PageHeader title={t("errors.loadDetailFailed")} />
      </div>
    );
  }

  const versions = versionsQuery.data?.items ?? [];
  const isSystem = template.org_id === null;
  const isArchived = Boolean(template.deleted_at);
  const { version: previewVersion, isDraftFallback } = pickPreviewVersion(versions);
  const latestPublished = versions
    .filter((v) => v.status === "published")
    .sort((a, b) => b.version - a.version)[0];

  const language: "en" | "es" = i18n.language.startsWith("es") ? "es" : "en";

  // The wire definition is an opaque JSON object; the draft-PUT size gate and
  // the publish gates own shape validation. Guard on `sections` before
  // handing the document to the renderer — drafts save unvalidated.
  const previewDefinition = previewVersion
    ? parseFormDefinition(previewVersion.definition)
    : undefined;
  const previewRenderable =
    previewDefinition !== undefined && Array.isArray(previewDefinition.sections);

  const openBuilder = (templateId: string) => {
    void navigate({ to: "/test-program/templates/$id/builder", params: { id: templateId } });
  };

  const handleNewDraft = () => {
    // A new draft is a v2 document: the builder authors v2 constructs, and v1
    // is a strict subset of v2, so up-stamping a copied published document is
    // always safe.
    const body: FormTemplateDraftPut = latestPublished
      ? {
          definition: {
            ...latestPublished.definition,
            schema_version: BUILDER_SCHEMA_VERSION,
          },
          review_required: latestPublished.review_required,
        }
      : {
          definition: {
            schema_version: BUILDER_SCHEMA_VERSION,
            target: template.target_kind === "device_test" ? "device" : "premise",
            sections: [],
          },
        };
    saveDraftMutation.mutate(body, {
      onSuccess: () => openBuilder(id),
      onError: () => toasts.error("form-templates:errors.newDraftFailed"),
    });
  };

  const handleForkSubmit = (values: TemplateMetaValues) => {
    setForkSlugConflict(null);
    setForkError(null);
    forkMutation.mutate(
      { slug: values.slug, title: values.title },
      {
        onSuccess: (created) => {
          toasts.success("form-templates:fork.success");
          setForkOpen(false);
          openBuilder(created.id);
        },
        onError: (err) => {
          // ApiError.details is `unknown`; the cast names only the OPTIONAL
          // `error` key, so any other envelope falls to the generic branch.
          if (
            err instanceof ApiError &&
            (err.details as { error?: string } | undefined)?.error === "form_template_slug_conflict"
          ) {
            setForkSlugConflict(t("errors.slugConflict"));
          } else {
            setForkError(t("errors.forkFailed"));
          }
        },
      },
    );
  };

  const handleDeleteClick = () => {
    setDeleteEnvelope(null);
    setConfirmOpen(true);
  };

  const envelopeMorphed =
    deleteEnvelope?.error === "template_has_open_assignments" ||
    deleteEnvelope?.error === "form_template_record_immutable_post_soft_delete";

  const handleDeleteConfirm = () => {
    if (envelopeMorphed) {
      setConfirmOpen(false);
      setDeleteEnvelope(null);
      return;
    }
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        toasts.success("form-templates:detail.delete.success");
        setConfirmOpen(false);
        void navigate({ to: "/test-program", search: { tab: "templates" } });
      },
      onError: (err) => {
        // ApiError.details is `unknown`. The two literal tests below verify the
        // `error` discriminant at runtime; `total_count` and
        // `dependent_assignment_ids`, read later, ride the same cast and are
        // required by the declared 409 schema for exactly those discriminants.
        const envelope =
          err instanceof ApiError
            ? (err.details as FormTemplateDeleteConflictError | undefined)
            : undefined;
        if (
          envelope?.error === "template_has_open_assignments" ||
          envelope?.error === "form_template_record_immutable_post_soft_delete"
        ) {
          setDeleteEnvelope(envelope);
        } else {
          toasts.error("form-templates:errors.deleteFailed");
          setConfirmOpen(false);
        }
      },
    });
  };

  const deleteMessage = () => {
    if (deleteEnvelope?.error === "template_has_open_assignments") {
      const base = t("detail.delete.ownerGuardBody", { count: deleteEnvelope.total_count });
      const ids = deleteEnvelope.dependent_assignment_ids;
      return ids.length > 0
        ? `${base} ${t("detail.delete.ownerGuardHint", { ids: ids.join(", ") })}`
        : base;
    }
    if (deleteEnvelope?.error === "form_template_record_immutable_post_soft_delete") {
      return t("errors.alreadyDeleted");
    }
    return t("detail.delete.body");
  };

  const tabs: TabDef<TemplateTab>[] = [
    { value: "definition", label: t("detail.tabs.definition") },
    { value: "versions", label: t("detail.tabs.versions"), count: versions.length },
  ];

  return (
    <div className="p-6">
      <Breadcrumbs
        items={[
          {
            label: t("testProgram.templates", { ns: "nav" }),
            to: "/test-program",
            search: { tab: "templates" },
          },
          { label: template.title },
        ]}
      />
      <PageHeader
        title={template.title}
        meta={
          <div className="flex items-center gap-2">
            <Badge variant={isSystem ? "info" : "default"}>{t(`source.${template.source}`)}</Badge>
            <Badge variant="default">{t(`targetKind.${template.target_kind}`)}</Badge>
            {isArchived && <Badge variant="danger">{t("detail.deletedBadge")}</Badge>}
          </div>
        }
        actions={
          !isArchived && (canUpdate || canCreate || canDelete) ? (
            <div className="flex items-center gap-2">
              {!isSystem &&
                canUpdate &&
                (template.has_draft ? (
                  <Button onClick={() => openBuilder(id)}>{t("detail.openBuilderCta")}</Button>
                ) : (
                  <Button onClick={handleNewDraft} disabled={saveDraftMutation.isPending}>
                    {t("detail.newDraftCta")}
                  </Button>
                ))}
              {isSystem && canCreate && (
                <Button
                  onClick={() => {
                    setForkSlugConflict(null);
                    setForkError(null);
                    setForkOpen(true);
                  }}
                >
                  {t("detail.forkCta")}
                </Button>
              )}
              {!isSystem && canDelete && (
                <Button variant="danger" onClick={handleDeleteClick}>
                  {t("detail.delete.title")}
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      <div className="mt-6">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={t("entity.singular")}
        />

        <div className="mt-6" role="tabpanel">
          {activeTab === "definition" && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <Card title={t("detail.meta.title")}>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-muted">{t("detail.meta.slug")}</dt>
                      <dd className="font-mono text-on-surface">{template.slug}</dd>
                    </div>
                    <div>
                      <dt className="text-muted">{t("detail.meta.targetKind")}</dt>
                      <dd className="text-on-surface">{t(`targetKind.${template.target_kind}`)}</dd>
                    </div>
                    {template.target_kind === "device_test" && (
                      <div>
                        <dt className="text-muted">{t("detail.meta.deviceCategory")}</dt>
                        <dd className="text-on-surface">
                          {template.device_category ? (
                            t(`vocabulary:device_category.${template.device_category}`)
                          ) : (
                            <EmptyValue />
                          )}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-muted">{t("detail.meta.source")}</dt>
                      <dd className="text-on-surface">{t(`source.${template.source}`)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted">{t("detail.meta.latestVersion")}</dt>
                      <dd className="text-on-surface">
                        {typeof template.latest_published_version === "number" ? (
                          t("versions.versionLabel", {
                            number: template.latest_published_version,
                          })
                        ) : (
                          <EmptyValue />
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted">{t("detail.meta.created")}</dt>
                      <dd className="text-on-surface">{formatDate(template.created_at)}</dd>
                    </div>
                    <div>
                      <dt className="text-muted">{t("detail.meta.updated")}</dt>
                      <dd className="text-on-surface">{formatDate(template.updated_at)}</dd>
                    </div>
                  </dl>
                </Card>
              </div>
              <div className="lg:col-span-8">
                <Card title={t("detail.previewTitle")}>
                  {isDraftFallback && (
                    <p className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                      {t("detail.previewingDraft")}
                    </p>
                  )}
                  {previewRenderable && previewDefinition ? (
                    <FormRenderer
                      definition={previewDefinition}
                      answers={{}}
                      language={language}
                      facilityRefSlot={renderFacilityRefSlot}
                    />
                  ) : previewVersion ? (
                    <p className="text-sm text-muted">{t("detail.previewUnavailable")}</p>
                  ) : (
                    <EmptyState title={t("detail.noVersions")} variant="inline" />
                  )}
                </Card>
              </div>
            </div>
          )}
          {activeTab === "versions" && (
            <VersionHistoryCard
              templateId={id}
              versions={versions}
              canRetire={canUpdate && !isSystem && !isArchived}
            />
          )}
        </div>
      </div>

      <DialogShell
        open={forkOpen}
        onClose={() => setForkOpen(false)}
        ariaLabelledBy="fork-template-detail-title"
      >
        <h2 id="fork-template-detail-title" className="text-lg font-semibold text-on-surface">
          {t("fork.title")}
        </h2>
        <p className="mt-1 text-sm text-muted">{t("fork.explainer")}</p>
        <div className="mt-4">
          {forkOpen && (
            <TemplateMetaForm
              mode="fork"
              initialTitle={template.title}
              isSubmitting={forkMutation.isPending}
              submitError={forkError}
              slugConflictError={forkSlugConflict}
              onSubmit={handleForkSubmit}
              onCancel={() => setForkOpen(false)}
            />
          )}
        </div>
      </DialogShell>

      <ConfirmDialog
        open={confirmOpen}
        onCancel={() => {
          setConfirmOpen(false);
          setDeleteEnvelope(null);
        }}
        onConfirm={handleDeleteConfirm}
        confirmDisabled={deleteMutation.isPending}
        title={
          deleteEnvelope?.error === "template_has_open_assignments"
            ? t("detail.delete.ownerGuardTitle")
            : t("detail.delete.title")
        }
        message={deleteMessage()}
        confirmLabel={
          envelopeMorphed ? t("detail.delete.ownerGuardCta") : t("detail.delete.confirm")
        }
        cancelLabel={t("detail.delete.cancel")}
        variant={envelopeMorphed ? "primary" : "danger"}
      />
    </div>
  );
}
